import { Typography, useMediaQuery, useTheme } from '@mui/material';
import './Home.scss';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import desktopViewTable from '../../components/TableTemplates/DesktopViewTable';
import mobileViewTable from '../../components/TableTemplates/MobileViewTable';
import { useNavigate } from 'react-router-dom';
import useHandleAnchorClick from '../../hooks/useHandleAnchorClick';
import { useTranslation } from 'react-i18next';
import NDCNews from './NDCNews';
import ImageSlider from './Carousel/Carousel';
import Marquee from 'react-fast-marquee';
import { ReactComponent as ArrowRight } from '../../assets/images/chevron-right.svg';

import NewsletterBanner from './NewsletterBanner/NewsletterBanner';
import SuccessStoriesCarousel from './SuccessStories/SuccessStoriesCarousel';

const parser = new DOMParser();

interface Content {
  infoBoxes: {
    [key: string]: {
      link: string;
      image: string;
      header: string;
      content: string[];
    };
  };
  explore: {
    links: {
      [key: string]: {
        link: string;
        image: string;
        text: string;
      };
    };
    infoBoxes: {
      [key: string]: {
        image: string;
        header: string;
        subheader: string;
        links: string[];
      };
    };
  };
  news: {
    releases: {
      [key: string]: {
        header: string;
        link: string;
        text: string;
      };
    };
    image: string;
    header: string;
  };
  keyFeatures: {
    header: string;
    bodyText: string;
    table: string;
  };
  mainHeader: string;
  mainSubHead: string;
}

const Home = ({ logoHeight }: { logoHeight: number }) => {
  const theme = useTheme();
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1200px)');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const content = t('ndc-program.home', {
    returnObjects: true,
    defaultValue: []
  }) as Content;
  const handleAnchorClick = useHandleAnchorClick();
  const isNotDekstop = useMediaQuery(theme.breakpoints.down(768));
  const getTableContent = (table) => {
    if (isMobile) {
      //check for colspan, in which case replace with equiv <td> for each as required
      const tableHTML = parser.parseFromString(table, 'text/html');

      let tdList = tableHTML.querySelectorAll('td');
      for (let i = 0; i < tdList.length; i++) {
        let item = tdList[i];
        if (item.colSpan && item.colSpan === 3) {
          //colspan = 3 means regular colspan
          let tdString = '';
          for (let i = 1; i <= 3; i++) {
            tdString = `${tdString}<td>${item.innerHTML}</td>`;
          }
          table = table.replaceAll(
            `<td colspan='3'>${item.innerHTML}</td>`,
            tdString
          );
        }
        //now convert all subsections of tables into their own tables
      }

      return mobileViewTable(table, 'NDC Home Table', handleAnchorClick);
    } else {
      return desktopViewTable(table, 'ndcHomeTable', 'NDC Home Table');
    }
  };
  const goToConnTab = (tab) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${i18n.language}/ndc-program/connection-options/${tab}`);
  };

  const handleNavigation = (link) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${i18n.language}${link}`);
  };

  return (
    content && (
      <div style={{ marginTop: `${logoHeight - 60}px` }}>
        {/* <div className={`homeBGimg ${isMobile && 'homeMobile'}`}></div> */}
        <ImageSlider logoHeight={logoHeight} />

        <main className={`homeBody ${isMobile && 'homeMobile'}`}>
          {/* <section className={`headerBox ${isMobile && "homeMobile"}`} aria-labelledby="homePageHeader">
            <h1 id="homePageHeader" aria-hidden="true">{t('ndc-program.home.headerBox.heading')}</h1>
            <p>{t('ndc-program.home.headerBox.subHeading')}</p>
            <Button
              variant="contained"
              onClick={() =>
                handleNavigation(t('ndc-program.home.headerBox.button.link'))
              }
              onKeyDown={() =>
                handleNavigation(t('ndc-program.home.headerBox.button.link'))
              }
            >
              {t('ndc-program.home.headerBox.button.text')}
            </Button>
          </section> */}
          <div
            className="headerSection center-width"
            aria-labelledby="distributionCapabilties"
          >
            <div className={`homeBGcolor ${isMobile && 'homeMobile'}`}></div>
            <h2
              className="mainHeader"
              dangerouslySetInnerHTML={{
                __html: t('ndc-program.home.mainHeader').toString()
              }}
              id="distributionCapabilties"
            ></h2>
            <div className="mainSubHeader">
              {t('ndc-program.home.mainSubHead')}
            </div>
            <div className="homeInfoBoxes">
              {Object.keys(content.infoBoxes).map((c, i) => (
                <div
                  className={`homeInfoBox ${
                    isMobile && 'homeInfoBoxMobile'
                  } capabilitiesBox`}
                  key={`info-${c}-${i}`}
                  onClick={() =>
                    handleNavigation(t(`ndc-program.home.infoBoxes.${c}.link`))
                  }
                  onKeyDown={() =>
                    handleNavigation(t(`ndc-program.home.infoBoxes.${c}.link`))
                  }
                  role="button"
                >
                  <img
                    src={`/images/discover/${t(
                      `ndc-program.home.infoBoxes.${c}.image`
                    )}.png`}
                    alt={t(`ndc-program.home.infoBoxes.${c}.header`)}
                    className="infoBoxImage"
                  />

                  <div className="infoBoxHeader">
                    {t(`ndc-program.home.infoBoxes.${c}.header`)}
                  </div>
                  <ChevronRightIcon
                    fontSize="medium"
                    sx={{
                      color: 'black',
                      display: 'inline-block',
                      position: 'absolute',
                      top: '35px',
                      right: '10px'
                    }}
                  />
                  <ul>
                    {content.infoBoxes[c].content.map((cItem, i) => (
                      <li key={`${c}-${i}`}>
                        {t(`ndc-program.home.infoBoxes.${c}.content.${i}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="center-width">
            <div className="keyFeatures" aria-labelledby="keyFeaturesID">
              <h2
                className="mainHeader"
                dangerouslySetInnerHTML={{
                  __html: t('ndc-program.home.keyFeatures.header') || ''
                }}
                id="keyFeaturesID"
              ></h2>
              <div
                className="mainSubHeader "
                dangerouslySetInnerHTML={{
                  __html: t('ndc-program.home.keyFeatures.bodyText') || ''
                }}
              ></div>
              {getTableContent(t('ndc-program.home.keyFeatures.table'))}
            </div>
          </div>
          {/* <div className="homeVideoBox" aria-labelledby="newProgramSection">
            <h2
              className="mainHeader"
              dangerouslySetInnerHTML={{
                __html: t('ndc-program.home.video.header').toString()
              }}
              id="newProgramSection"
            ></h2>
            <AppVideo src={t('ndc-program.home.video.src')} />
          </div> */}
          <div className="center-width">
            <div className="exploreOptions" aria-labelledby="exploreOptions">
              <h2
                className="mainHeader"
                dangerouslySetInnerHTML={{
                  __html: t('ndc-program.home.explore.header').toString()
                }}
                id="exploreOptions"
              ></h2>
              <div
                className="mainSubHeader"
                dangerouslySetInnerHTML={{
                  __html: t('ndc-program.home.explore.subHeading').toString()
                }}
              ></div>
              <div className="exploreLinks">
                {Object.keys(content.explore.links).map((c, i) => (
                  <div
                    className="exploreLink"
                    key={`exp-${c}`}
                    onClick={() =>
                      goToConnTab(t(`ndc-program.home.explore.links.${c}.link`))
                    }
                    onKeyDown={() =>
                      goToConnTab(t(`ndc-program.home.explore.links.${c}.link`))
                    }
                    role="button"
                  >
                    <div className="exploreIconBG">
                      <img
                        src={`/images/discover/${t(
                          `ndc-program.home.explore.links.${c}.image`
                        )}.png`}
                        alt={t(`ndc-program.home.explore.links.${c}.text`)}
                        className="exploreIcons"
                      />
                    </div>
                    <div
                      className={`exploreText ${
                        isMobile && 'exploreTextMobile'
                      }`}
                    >
                      {t(`ndc-program.home.explore.links.${c}.text`)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="exploreCards">
                {Object.keys(content.explore.infoBoxes).map((ib, i) => (
                  <div
                    className={`homeInfoBox ${isMobile && 'homeInfoBoxMobile'}`}
                    key={`expCard-${ib}`}
                  >
                    <img
                      src={`/images/discover/${t(
                        `ndc-program.home.explore.infoBoxes.${ib}.image`
                      )}.jpeg`}
                      alt={t(`ndc-program.home.explore.infoBoxes.${ib}.header`)}
                      className="exploreInfoBoxImg"
                    />
                    <div className="infoBoxHeader">
                      {t(`ndc-program.home.explore.infoBoxes.${ib}.header`)}
                    </div>
                    {t(`ndc-program.home.explore.infoBoxes.${ib}.subheader`) !==
                      `ndc-program.home.explore.infoBoxes.${ib}.subheader` && (
                      <div className="infoBoxSubhead">
                        {t(
                          `ndc-program.home.explore.infoBoxes.${ib}.subheader`
                        )}
                      </div>
                    )}
                    <div className="infoBoxExploreIcons">
                      {content.explore.infoBoxes[ib]['links'].map((ibl, i) => {
                        return (
                          <div
                            className="exploreIconBG"
                            key={`infoExpIcon-${ibl}`}
                            onClick={() =>
                              goToConnTab(
                                t(`ndc-program.home.explore.links.${ibl}.link`)
                              )
                            }
                            onKeyDown={() =>
                              goToConnTab(
                                t(`ndc-program.home.explore.links.${ibl}.link`)
                              )
                            }
                            role="button"
                          >
                            <img
                              src={`/images/discover/${t(
                                `ndc-program.home.explore.links.${ibl}.image`
                              )}.png`}
                              alt={t(
                                `ndc-program.home.explore.links.${ibl}.text`
                              )}
                              className="exploreIcons"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <section
            aria-roledescription="carousel"
            className="partnersSection"
            aria-label={t(
              'ndc-program.home.partners.accessibility.containerHead'
            )}
          >
            <div className="center-width">
              <h2
                id="partnersHeader"
                className="mainHeader"
                tabIndex={0}
                aria-label={t('ndc-program.home.partners.header')}
              >
                {t('ndc-program.home.partners.header')}
              </h2>

              <div id="partnersMarqueeContainer">
                <Marquee
                  gradient={false}
                  speed={30}
                  pauseOnHover
                  direction="left"
                  className="partnersMarquee"
                >
                  {(
                    t('ndc-program.home.partners.list', {
                      returnObjects: true,
                      defaultValue: []
                    }) as any
                  ).map(
                    (partner, index) =>
                      index % 2 === 1 && (
                        <img
                          key={index}
                          src={partner.image}
                          alt={`Logo of ${partner.name}`}
                          className="partnerLogo"
                        />
                      )
                  )}
                </Marquee>

                <Marquee
                  gradient={false}
                  speed={32}
                  pauseOnHover
                  delay={2}
                  direction="left"
                >
                  {(
                    t('ndc-program.home.partners.list', {
                      returnObjects: true,
                      defaultValue: []
                    }) as any
                  ).map(
                    (partner, index) =>
                      index % 2 === 0 && (
                        <img
                          key={index}
                          src={partner.image}
                          alt={`Logo of ${partner.name}`}
                          className="partnerLogo"
                        />
                      )
                  )}
                </Marquee>
              </div>

              <div
                className="partnersLink"
                role="link"
                tabIndex={0}
                aria-label={
                  isNotDekstop
                    ? t('ndc-program.home.partners.shortLinkText')
                    : t('ndc-program.home.partners.linkText')
                }
                onClick={() =>
                  navigate(t('ndc-program.home.partners.link') as string)
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(t('ndc-program.home.partners.link') as string);
                  }
                }}
              >
                {isNotDekstop
                  ? t('ndc-program.home.partners.shortLinkText')
                  : t('ndc-program.home.partners.linkText')}
                <ArrowRight className="arrowRight" />
              </div>
            </div>
          </section>

          <section
            className="center-width"
            aria-labelledby="quotes-heading"
          >
            {/* <div className="center-width"> */}
              <SuccessStoriesCarousel
                data={t('SuccessStories', {
                  returnObjects: true,
                  defaultValue: []
                })}
              />
            {/* </div> */}
          </section>

          <div className="center-width" id="newsletter-banner">
            <NewsletterBanner />
          </div>

          <section
            style={{
              backgroundColor: '#F9F9F9',
              marginTop: isMobile ? '15px' : isTablet ? '25px' : '50px',
              marginLeft: isMobile ? '-40px' : isTablet ? '-60px' : 'auto',
              marginRight: isMobile ? '-40px' : isTablet ? '-60px' : 'auto'
            }}
            className="newsBox"
            // role="region"
            aria-live="polite"
          >
            <div className="center-width">
              <Typography
                id="newBoxSection"
                component="h2"
                sx={{
                  fontFamily: 'Open Sans',
                  fontWeight: '700 !important',
                  fontSize: '36px',
                  lineHeight: '48px',
                  color: '#000000',
                  pl: isMobile ? 9 : 12,
                  '@media (min-width:768px) and (max-width:1200px)': {
                    paddingLeft: 10
                  },
                  pt: 5
                  // justifyContent: "left"
                }}
              >
                {t('ndc_news_var.title')}
              </Typography>
              <div aria-labelledby="newBoxSection">
                <NDCNews
                  data={t('ndc_news', {
                    returnObjects: true,
                    defaultValue: []
                  })}
                  // onContentUpdate={() => {
                  //   const firstArticle = document.querySelector(
                  //     '[role="article"]'
                  //   ) as HTMLElement;
                  //   if (firstArticle) firstArticle.focus();
                  // }}
                />
              </div>
              {/* <h2
              className="mainHeader"
              dangerouslySetInnerHTML={{ __html: t('ndc-program.home.news.header') || '' }}
              id="newBoxSection"
            ></h2>
            <img
              src={`/images/discover/${t('ndc-program.home.news.image')}.jpg`}
              alt="NDC News"
              className="newsImage"
            />
            <div className={`newsReleases ${isMobile && "newsReleasesMobile"}`}>
              <ul>
                {Object.keys(content.news.releases).map((r, i) => {
                  return (
                    <li className="newsRelease" key={`news-${r}`}>
                      <p>{t(`ndc-program.home.news.releases.${r}.header`)}</p>
                      <p><a href={t(`ndc-program.home.news.releases.${r}.link`)}
                        target="_blank"
                        rel="noreferrer">
                        {t(`ndc-program.home.news.releases.${r}.text`)}</a></p>
                    </li>
                  );
                })}
              </ul>
            </div> */}
            </div>
          </section>
        </main>
      </div>
    )
  );
};

export default Home;
